import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { TreatmentStage } from '../../../../types/admin';
import { useUpdateTreatmentStage } from '../../hooks/usePatientMutations';
import { formatMoney } from '../plan-options/pricing';
import styles from '../StepPanel.module.scss';

export type CycleSettingsFormProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
  /** Lets the panel flag its tab while edits sit unsaved behind a hidden panel. */
  readonly onDirtyChange?: (isDirty: boolean) => void;
};

function textEquals(value: string, saved: string | null | undefined): boolean {
  return value.trim() === (saved ?? '').trim();
}

/**
 * Money fields round-trip as decimal strings ("1000" comes back as "1000.00"),
 * so a plain string compare would leave the form permanently "unsaved".
 */
function moneyEquals(value: string, saved: string | null | undefined): boolean {
  const parse = (input: string | null | undefined): number | null => {
    if (input === null || input === undefined || input.trim() === '') return null;
    const parsed = Number.parseFloat(input);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const current = parse(value);
  const stored = parse(saved);
  if (current === null || stored === null) return current === stored;
  return Math.abs(current - stored) < 0.005;
}

/**
 * Cycle-wide modeling settings: the visualization caption plus the pricing every
 * plan option of this cycle is costed against.
 */
export function CycleSettingsForm({
  patientId,
  treatmentId,
  stage,
  onDirtyChange,
}: CycleSettingsFormProps) {
  const { t } = useTranslation();
  const updateStageMutation = useUpdateTreatmentStage(patientId, treatmentId, stage.id);

  const [visualizationTitle, setVisualizationTitle] = useState(stage.visualization_title ?? '');
  const [visualizationDescription, setVisualizationDescription] = useState(
    stage.visualization_description ?? '',
  );
  const [alignerUnitPrice, setAlignerUnitPrice] = useState(stage.aligner_unit_price ?? '');
  const [prepaymentAmount, setPrepaymentAmount] = useState(stage.prepayment_amount ?? '');
  const [discountAmount, setDiscountAmount] = useState(stage.discount_amount ?? '');
  const [pricingCurrency, setPricingCurrency] = useState(stage.pricing_currency ?? 'UAH');

  const unitPriceNum = alignerUnitPrice ? parseFloat(alignerUnitPrice) : NaN;
  const prepayNum = prepaymentAmount ? parseFloat(prepaymentAmount) : 0;
  const discountNum = discountAmount ? parseFloat(discountAmount) : 0;
  const currencyCode = pricingCurrency || 'UAH';

  const isDirty =
    !textEquals(visualizationTitle, stage.visualization_title) ||
    !textEquals(visualizationDescription, stage.visualization_description) ||
    !moneyEquals(alignerUnitPrice, stage.aligner_unit_price) ||
    !moneyEquals(prepaymentAmount, stage.prepayment_amount) ||
    !moneyEquals(discountAmount, stage.discount_amount) ||
    !textEquals(pricingCurrency, stage.pricing_currency ?? 'UAH');

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedCurrency = pricingCurrency.trim();
    const trimmedUnitPrice = alignerUnitPrice.trim();
    const trimmedPrepay = prepaymentAmount.trim();
    const trimmedDiscount = discountAmount.trim();

    try {
      await updateStageMutation.mutateAsync({
        visualization_title: visualizationTitle.trim() ? visualizationTitle.trim() : null,
        visualization_description: visualizationDescription.trim()
          ? visualizationDescription.trim()
          : null,
        aligner_unit_price: trimmedUnitPrice ? trimmedUnitPrice : null,
        prepayment_amount: trimmedPrepay ? trimmedPrepay : null,
        discount_amount: trimmedDiscount ? trimmedDiscount : null,
        pricing_currency: trimmedCurrency ? trimmedCurrency : null,
      });
    } catch {
      return;
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.settingsForm}>
      <div className={styles.settingsColumns}>
        <section className={styles.formCard}>
          <h5 className={styles.formCardTitle}>{t('modeling.pricingSection')}</h5>
          <p className={styles.formCardHint}>{t('modeling.pricingSharedHint')}</p>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span>{t('modeling.alignerUnitPrice')}</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={alignerUnitPrice}
                onChange={(e) => setAlignerUnitPrice(e.target.value)}
                placeholder="0.00"
              />
            </label>
            <label className={styles.field}>
              <span>{t('modeling.discount')}</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                placeholder="0.00"
              />
            </label>
            <label className={styles.field}>
              <span>{t('modeling.prepayment')}</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={prepaymentAmount}
                onChange={(e) => setPrepaymentAmount(e.target.value)}
                placeholder="0.00"
              />
            </label>
            <label className={styles.field}>
              <span>{t('modeling.pricingCurrency')}</span>
              <input
                type="text"
                maxLength={8}
                value={pricingCurrency}
                onChange={(e) => setPricingCurrency(e.target.value)}
                placeholder="UAH"
              />
            </label>
          </div>
          {Number.isFinite(unitPriceNum) && unitPriceNum > 0 && (
            <div className={styles.pricingSummary} aria-label={t('modeling.pricingPreview')}>
              <div className={styles.pricingSummaryRow}>
                <span>{t('modeling.alignerUnitPrice')}</span>
                <span className={styles.pricingSummaryValue}>
                  {formatMoney(unitPriceNum, currencyCode)}
                </span>
              </div>
              {discountNum > 0 && (
                <div className={styles.pricingSummaryRow}>
                  <span>{t('modeling.discount')}</span>
                  <span
                    className={`${styles.pricingSummaryValue} ${styles.pricingSummaryValueNegative}`}
                  >
                    −{formatMoney(discountNum, currencyCode)}
                  </span>
                </div>
              )}
              {prepayNum > 0 && (
                <div className={styles.pricingSummaryRow}>
                  <span>{t('modeling.prepayment')}</span>
                  <span
                    className={`${styles.pricingSummaryValue} ${styles.pricingSummaryValueNegative}`}
                  >
                    −{formatMoney(prepayNum, currencyCode)}
                  </span>
                </div>
              )}
            </div>
          )}
        </section>

        <section className={styles.formCard}>
          <h5 className={styles.formCardTitle}>{t('modeling.planSection')}</h5>
          <p className={styles.formCardHint}>{t('modeling.visualizationHint')}</p>
          <label className={styles.field}>
            <span>{t('modeling.visualizationTitle')}</span>
            <input
              type="text"
              value={visualizationTitle}
              onChange={(e) => setVisualizationTitle(e.target.value)}
              placeholder={t('modeling.visualizationTitlePlaceholder')}
              maxLength={255}
            />
          </label>
          <label className={styles.field}>
            <span>{t('modeling.visualizationDescription')}</span>
            <textarea
              value={visualizationDescription}
              onChange={(e) => setVisualizationDescription(e.target.value)}
              placeholder={t('modeling.visualizationDescriptionPlaceholder')}
              rows={4}
              maxLength={4000}
            />
          </label>
        </section>
      </div>

      <div className={styles.formFooterBar}>
        {updateStageMutation.error instanceof Error && (
          <p className={styles.inlineError}>{updateStageMutation.error.message}</p>
        )}
        {isDirty && !updateStageMutation.isPending && (
          <span className={styles.dirtyChip} data-testid="settings-dirty">
            {t('modeling.unsavedChanges')}
          </span>
        )}
        <button
          type="submit"
          className={styles.primaryBtn}
          disabled={updateStageMutation.isPending}
        >
          {updateStageMutation.isPending ? t('modeling.saving') : t('modeling.saveSettings')}
        </button>
      </div>
    </form>
  );
}
